import { useState, useEffect, useRef, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { faqService, uploadService } from '../../services/api';
import toast from 'react-hot-toast';

const FAQManage = () => {
  const [categories, setCategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Modal states
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  // File upload states
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const quillRef = useRef(null);

  // Form states
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    categoryId: '',
    keywords: '',
    isFeatured: false,
    isActive: true,
  });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    isActive: true,
  });

  // Quill image handler
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        try {
          setUploading(true);
          const response = await uploadService.uploadImage(file);
          const imageUrl = import.meta.env.VITE_API_BASE_URL
            ? `${import.meta.env.VITE_API_BASE_URL}${response.data.data.url}`
            : `http://localhost:5001${response.data.data.url}`;

          const quill = quillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, 'image', imageUrl);
            quill.setSelection(range.index + 1);
          }
        } catch (error) {
          toast.error('Failed to upload image');
        } finally {
          setUploading(false);
        }
      }
    };
  };

  // Quill modules configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
  }), []);

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent',
    'blockquote', 'code-block', 'link', 'image'
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchFaqs(selectedCategory);
    } else {
      setFaqs([]);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await faqService.getCategories();
      const cats = response.data.data || [];
      setCategories(cats);
      if (cats.length > 0 && !selectedCategory) {
        setSelectedCategory(cats[0].id);
      }
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchFaqs = async (categoryId) => {
    try {
      const response = await faqService.getAll({ categoryId });
      setFaqs(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load FAQs');
    }
  };

  // File upload handlers
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(file => uploadService.uploadFile(file));
      const responses = await Promise.all(uploadPromises);

      const newAttachments = responses.map(res => ({
        ...res.data.data,
        url: import.meta.env.VITE_API_BASE_URL
          ? `${import.meta.env.VITE_API_BASE_URL}${res.data.data.url}`
          : `http://localhost:5001${res.data.data.url}`
      }));

      setAttachments(prev => [...prev, ...newAttachments]);
      toast.success(`${files.length} file(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload file(s)');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // FAQ handlers
  const handleAddFaq = () => {
    setEditingFaq(null);
    setFaqForm({
      question: '',
      answer: '',
      categoryId: selectedCategory || '',
      keywords: '',
      isFeatured: false,
      isActive: true,
    });
    setAttachments([]);
    setShowFaqModal(true);
  };

  const handleEditFaq = (faq) => {
    setEditingFaq(faq);
    setFaqForm({
      question: faq.question || '',
      answer: faq.answer || '',
      categoryId: faq.category_id || '',
      keywords: Array.isArray(faq.keywords) ? faq.keywords.join(', ') : '',
      isFeatured: faq.is_featured || false,
      isActive: faq.is_active !== false,
    });
    setAttachments(faq.attachments || []);
    setShowFaqModal(true);
  };

  const handleSaveFaq = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = {
      ...faqForm,
      keywords: faqForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
      attachments: attachments,
    };

    try {
      if (editingFaq) {
        await faqService.update(editingFaq.id, data);
        toast.success('FAQ updated successfully');
      } else {
        await faqService.create(data);
        toast.success('FAQ created successfully');
      }
      setShowFaqModal(false);
      fetchFaqs(selectedCategory);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFaq = (faq) => {
    setDeleteTarget({ type: 'faq', item: faq });
    setShowDeleteModal(true);
  };

  // Category handlers
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      slug: '',
      description: '',
      icon: '',
      isActive: true,
    });
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || '',
      slug: category.slug || '',
      description: category.description || '',
      icon: category.icon || '',
      isActive: category.is_active !== false,
    });
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = {
      ...categoryForm,
      slug: categoryForm.slug || categoryForm.name.toLowerCase().replace(/\s+/g, '-'),
    };

    try {
      if (editingCategory) {
        await faqService.updateCategory(editingCategory.id, data);
        toast.success('Category updated successfully');
      } else {
        await faqService.createCategory(data);
        toast.success('Category created successfully');
      }
      setShowCategoryModal(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = (category) => {
    setDeleteTarget({ type: 'category', item: category });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      if (deleteTarget.type === 'faq') {
        await faqService.delete(deleteTarget.item.id);
        toast.success('FAQ deleted successfully');
        fetchFaqs(selectedCategory);
      } else {
        await faqService.deleteCategory(deleteTarget.item.id);
        toast.success('Category deleted successfully');
        if (selectedCategory === deleteTarget.item.id) {
          setSelectedCategory(null);
        }
        fetchCategories();
      }
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage FAQs</h1>
          <p className="text-gray-600">Create and manage knowledge base content</p>
        </div>
        <button onClick={handleAddFaq} className="btn-primary">
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add FAQ
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Categories */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="font-medium">Categories</h2>
              <button
                onClick={handleAddCategory}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                + Add
              </button>
            </div>
            <div className="divide-y divide-gray-200">
              {categories.length > 0 ? (
                categories.map((category) => (
                  <div
                    key={category.id}
                    className={`group flex items-center justify-between px-4 py-3 text-sm hover:bg-gray-50 transition-colors cursor-pointer ${
                      selectedCategory === category.id ? 'bg-primary-50' : ''
                    }`}
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <span className={`block truncate ${
                        selectedCategory === category.id ? 'text-primary-700 font-medium' : 'text-gray-700'
                      }`}>
                        {category.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {category.faq_count || 0} FAQs
                      </span>
                    </div>
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditCategory(category); }}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Edit category"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category); }}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete category"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-4 py-8 text-center text-gray-500 text-sm">
                  No categories yet
                </div>
              )}
            </div>
          </div>
        </div>

        {/* FAQ List */}
        <div className="lg:col-span-3">
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="font-medium">
                {categories.find(c => c.id === selectedCategory)?.name || 'Select a category'}
              </h2>
              {selectedCategory && (
                <span className="text-sm text-gray-500">{faqs.length} FAQs</span>
              )}
            </div>
            {!selectedCategory ? (
              <div className="p-8 text-center text-gray-500">
                <p>Select a category to view FAQs</p>
              </div>
            ) : faqs.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {faqs.map((faq) => (
                  <div key={faq.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900">{faq.question}</h3>
                        <div
                          className="text-sm text-gray-500 mt-1 line-clamp-2 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: faq.answer.length > 150
                              ? faq.answer.substring(0, 150).replace(/<[^>]*>/g, '') + '...'
                              : faq.answer.replace(/<[^>]*>/g, '')
                          }}
                        />
                        <div className="flex items-center flex-wrap gap-2 mt-2">
                          <span className="text-xs text-gray-400">{faq.view_count || 0} views</span>
                          <span className="text-xs text-gray-400">{faq.helpful_count || 0} helpful</span>
                          {faq.is_featured && <span className="badge badge-blue">Featured</span>}
                          {!faq.is_active && <span className="badge badge-red">Inactive</span>}
                          {faq.attachments?.length > 0 && (
                            <span className="badge badge-gray">{faq.attachments.length} attachment(s)</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 ml-4">
                        <button
                          onClick={() => handleEditFaq(faq)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit FAQ"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteFaq(faq)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete FAQ"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <svg className="h-12 w-12 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>No FAQs in this category</p>
                <button onClick={handleAddFaq} className="btn-primary btn-sm mt-4">
                  Add FAQ
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FAQ Modal with WYSIWYG Editor */}
      {showFaqModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowFaqModal(false)}></div>

            <div className="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full mx-auto z-10">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
                </h3>
                {uploading && (
                  <span className="text-sm text-primary-600 flex items-center">
                    <div className="spinner spinner-sm mr-2"></div>
                    Uploading...
                  </span>
                )}
              </div>

              <form onSubmit={handleSaveFaq}>
                <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Category</label>
                      <select
                        value={faqForm.categoryId}
                        onChange={(e) => setFaqForm({ ...faqForm, categoryId: e.target.value })}
                        className="select"
                        required
                      >
                        <option value="">Select category...</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label">Keywords (comma-separated)</label>
                      <input
                        type="text"
                        value={faqForm.keywords}
                        onChange={(e) => setFaqForm({ ...faqForm, keywords: e.target.value })}
                        className="input"
                        placeholder="e.g., password, login, reset"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">Question</label>
                    <input
                      type="text"
                      value={faqForm.question}
                      onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                      className="input"
                      placeholder="Enter the question..."
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Answer</label>
                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={faqForm.answer}
                        onChange={(value) => setFaqForm({ ...faqForm, answer: value })}
                        modules={modules}
                        formats={formats}
                        placeholder="Enter the answer with rich formatting..."
                        style={{ minHeight: '200px' }}
                      />
                    </div>
                  </div>

                  {/* File Attachments */}
                  <div>
                    <label className="label">Attachments</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                        id="file-upload"
                      />
                      <label
                        htmlFor="file-upload"
                        className="flex flex-col items-center cursor-pointer"
                      >
                        <svg className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="mt-2 text-sm text-gray-600">
                          Click to upload files or drag and drop
                        </span>
                        <span className="mt-1 text-xs text-gray-400">
                          PDF, DOC, XLS, PPT up to 20MB
                        </span>
                      </label>
                    </div>

                    {/* Uploaded files list */}
                    {attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {attachments.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center space-x-2 min-w-0">
                              <svg className="h-5 w-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-sm text-gray-700 truncate">{file.originalName}</span>
                              <span className="text-xs text-gray-400">({formatFileSize(file.size)})</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(index)}
                              className="p-1 text-gray-400 hover:text-red-600"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-6">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={faqForm.isFeatured}
                        onChange={(e) => setFaqForm({ ...faqForm, isFeatured: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Featured</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={faqForm.isActive}
                        onChange={(e) => setFaqForm({ ...faqForm, isActive: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                  <button type="button" onClick={() => setShowFaqModal(false)} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving || uploading} className="btn-primary">
                    {saving ? 'Saving...' : 'Save FAQ'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCategoryModal(false)}></div>

            <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-auto z-10">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingCategory ? 'Edit Category' : 'Add New Category'}
                </h3>
              </div>

              <form onSubmit={handleSaveCategory}>
                <div className="px-6 py-4 space-y-4">
                  <div>
                    <label className="label">Name</label>
                    <input
                      type="text"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      className="input"
                      placeholder="e.g., Getting Started"
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Slug (URL-friendly name)</label>
                    <input
                      type="text"
                      value={categoryForm.slug}
                      onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                      className="input"
                      placeholder="e.g., getting-started (auto-generated if empty)"
                    />
                  </div>

                  <div>
                    <label className="label">Description</label>
                    <textarea
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                      className="textarea"
                      rows={3}
                      placeholder="Brief description of this category..."
                    />
                  </div>

                  <div>
                    <label className="label">Icon (emoji or icon name)</label>
                    <input
                      type="text"
                      value={categoryForm.icon}
                      onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                      className="input"
                      placeholder="e.g., question, computer, settings"
                    />
                  </div>

                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={categoryForm.isActive}
                        onChange={(e) => setCategoryForm({ ...categoryForm, isActive: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                  <button type="button" onClick={() => setShowCategoryModal(false)} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving} className="btn-primary">
                    {saving ? 'Saving...' : 'Save Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteModal(false)}></div>

            <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full mx-auto z-10">
              <div className="px-6 py-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Delete {deleteTarget.type === 'faq' ? 'FAQ' : 'Category'}?
                </h3>
                <p className="text-sm text-gray-500 text-center">
                  {deleteTarget.type === 'faq'
                    ? `Are you sure you want to delete "${deleteTarget.item.question}"?`
                    : `Are you sure you want to delete the "${deleteTarget.item.name}" category? This may affect FAQs in this category.`
                  }
                </p>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button onClick={() => setShowDeleteModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={saving}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors"
                >
                  {saving ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quill Editor Styles */}
      <style>{`
        .ql-container {
          min-height: 200px;
          font-size: 14px;
        }
        .ql-editor {
          min-height: 200px;
        }
        .ql-toolbar {
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          background: #f9fafb;
        }
        .ql-container {
          border-bottom-left-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default FAQManage;
